import {
  ABC_DELIVERY_SERVICE_LABELS,
  ABC_DELIVERY_SERVICES,
  ABC_DELIVERY_TIME_LABELS,
  ABC_DELIVERY_TIMES,
  type AbcDeliveryService,
  type AbcDeliveryTime,
  type AbcOrderPreview,
  type AbcStatus,
  MATERIAL_ORDER_STATUS_LABELS,
  MEASUREMENT_SOURCE_LABELS,
  MEASUREMENT_SOURCES,
  type MaterialCatalogResponse,
  type MaterialItem,
  type MaterialLine,
  type MaterialOrderItem,
  type MaterialOrderStatus,
  type MeasurementItem,
  type MeasurementReading,
  type MeasureItemDef,
  type UploadSlots,
  squaresWithWaste,
  formatQuantity,
  lineFor,
  linesCost,
  orderProblems,
  withOption,
} from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, errorMessage, get, patch, post } from "../api";
import { AbcSearchDialog } from "../components/abc-search";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, TextAreaField, formatCents, formatDateTime } from "../components/ui";
import { useSession } from "../session";

const ORDER_TONE: Record<MaterialOrderStatus, "ok" | "wait" | "off" | "brand"> = { draft: "wait", ordered: "brand", delivered: "ok", cancelled: "off" };

export function OrderStatus({ status }: { status: MaterialOrderStatus }) {
  return <Badge tone={ORDER_TONE[status]}>{MATERIAL_ORDER_STATUS_LABELS[status]}</Badge>;
}

const deliveryText = (d: string | null) => (d ? new Date(`${d}T12:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "No delivery date");

/** Reads the job's measurement reports (the page polls while one is being read). */
export function useMeasurements(jobId: string) {
  return useQuery({
    queryKey: ["measurements", jobId],
    queryFn: () => get<MeasurementItem[]>(`/api/jobs/${jobId}/measurements`),
    refetchInterval: (q) => (q.state.data?.some((m) => m.status === "reading") ? 3000 : false),
  });
}

/** The job's Materials tab: measurement reports and material orders. */
export function JobMaterials({ jobId }: { jobId: string }) {
  return (
    <div className="flex flex-col gap-5">
      <Measurements jobId={jobId} />
      <JobOrders jobId={jobId} />
    </div>
  );
}

const KIND_LABEL: Record<MeasurementItem["kind"], string> = { report: "Report", manual: "Entered by hand", scope: "From the insurance scope" };

function Measurements({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const list = useMeasurements(jobId);
  const catalog = useQuery({ queryKey: ["material-catalog"], queryFn: () => get<MaterialCatalogResponse>("/api/material-catalog") });
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<MeasurementItem | null>(null);
  const [typing, setTyping] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["measurements", jobId] });
  const add = useMutation({
    mutationFn: async (f: File) => {
      const [slot] = await post<UploadSlots[]>(`/api/jobs/${jobId}/files/upload-urls`, { files: [{ name: f.name, contentType: "application/pdf", size: f.size }] });
      const res = await fetch(slot!.original.url, { method: "PUT", headers: slot!.original.headers, body: f });
      if (!res.ok) throw new Error("Upload failed. Try again.");
      const saved = await post<{ id: string }>(`/api/jobs/${jobId}/files`, { key: slot!.original.key, name: f.name, contentType: "application/pdf", size: f.size, category: "Measurements" });
      return post<MeasurementItem>(`/api/jobs/${jobId}/measurements`, { fileId: saved.id });
    },
    onSuccess: refresh,
  });
  const use = useMutation({ mutationFn: (id: string) => post(`/api/measurements/${id}/use`), onSuccess: refresh });
  const defs = catalog.data?.measurementItems ?? [];
  return (
    <Panel>
      <PanelHeader
        title="Roof measurements"
        description="From a Roofr, Hover or EagleView report (read for you), typed in, or the insurance scope. The one marked In use is what proposals, work orders and material orders take."
        action={
          can("materials.order") && (
            <div className="flex flex-wrap gap-2">
              <input
                ref={input}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) add.mutate(f);
                  e.target.value = "";
                }}
              />
              <Button className="px-3 py-1.5" onClick={() => setTyping(true)}>
                + Enter measurements
              </Button>
              <Button variant="primary" className="px-3 py-1.5" busy={add.isPending} onClick={() => input.current?.click()}>
                Upload report
              </Button>
            </div>
          )
        }
      />
      {(add.error || use.error) && (
        <div className="px-5 pt-4">
          <Alert>{errorMessage(add.error ?? use.error)}</Alert>
        </div>
      )}
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !list.data?.length ? (
        <Empty>No measurements yet.</Empty>
      ) : (
        <ul>
          {list.data.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-3 border-b border-mist px-5 py-3 last:border-0">
              {m.status === "reading" ? <Badge tone="wait">Reading…</Badge> : m.status === "read_failed" ? <Badge tone="off">Couldn't read</Badge> : m.inUse ? <Badge tone="ok">In use</Badge> : m.problems.length ? <Badge tone="wait">Check it</Badge> : <Badge tone="off">Not in use</Badge>}
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{[m.label, m.kind === "report" ? (m.file?.name ?? "Report") : KIND_LABEL[m.kind]].filter(Boolean).join(" · ")}</span>
                <span className="block text-sm text-muted">
                  {m.reading
                    ? `${m.kind === "report" ? `${MEASUREMENT_SOURCE_LABELS[m.reading.source]} · ` : ""}${m.reading.totalAreaSqFt.toLocaleString("en-US")} sq ft · ${formatQuantity(squaresWithWaste(m.reading.totalAreaSqFt, m.wastePercent))} SQ with ${m.wastePercent}% waste${m.createdBy ? ` · ${m.createdBy}` : ""}, ${formatDateTime(m.createdAt)}`
                    : (m.error ?? `Added ${formatDateTime(m.createdAt)}`)}
                </span>
              </span>
              {m.file && (
                <a href={m.file.url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-ink-soft hover:text-ink">
                  PDF
                </a>
              )}
              {can("materials.order") && m.status === "read" && !m.inUse && (
                <button className="text-sm font-semibold text-ink-soft hover:text-ink" disabled={use.isPending} onClick={() => use.mutate(m.id)}>
                  Use this one
                </button>
              )}
              {m.status !== "reading" && (
                <button className="text-sm font-semibold text-brand hover:underline" onClick={() => setOpen(m)}>
                  {m.reading ? "Numbers" : "Enter by hand"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && <ReadingDialog m={open} defs={defs} onClose={() => setOpen(null)} onSaved={async () => (await refresh(), setOpen(null))} />}
      {typing && <ReadingDialog jobId={jobId} defs={defs} wastePercent={catalog.data?.wastePercent ?? 10} onClose={() => setTyping(false)} onSaved={async () => (await refresh(), setTyping(false))} />}
    </Panel>
  );
}

const EMPTY: MeasurementReading = { source: "other", address: "", totalAreaSqFt: 0, pitches: [], facets: null, ridgeFt: 0, hipFt: 0, valleyFt: 0, eaveFt: 0, rakeFt: 0, stepFlashingFt: 0, wallFlashingFt: 0, suggestedWastePercent: null };
const LENGTHS: [keyof MeasurementReading, string][] = [
  ["ridgeFt", "Ridges"],
  ["hipFt", "Hips"],
  ["valleyFt", "Valleys"],
  ["eaveFt", "Eaves"],
  ["rakeFt", "Rakes"],
  ["stepFlashingFt", "Step flashing"],
  ["wallFlashingFt", "Wall flashing"],
];

/** A number box that keeps what's typed ("117." on the way to "117.5") and reports the number. */
function NumField({ label, value, onChange, disabled, hint }: { label: string; value: number; onChange: (n: number) => void; disabled?: boolean; hint?: string }) {
  const [text, setText] = useState(value ? String(value) : "");
  return (
    <Field
      label={label}
      inputMode="decimal"
      disabled={disabled}
      value={text}
      hint={hint}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value.replace(/,/g, ""));
        onChange(Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0);
      }}
    />
  );
}

/** The numbers of one measurement (or a new one typed in, when there's no `m`). */
function ReadingDialog({ m, jobId, defs, wastePercent, onClose, onSaved }: { m?: MeasurementItem; jobId?: string; defs: MeasureItemDef[]; wastePercent?: number; onClose: () => void; onSaved: () => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [r, setR] = useState<MeasurementReading>(m?.reading ?? EMPTY);
  const [waste, setWaste] = useState(String(m?.wastePercent ?? wastePercent ?? 10));
  const [label, setLabel] = useState(m?.label ?? "");
  const [notes, setNotes] = useState(m?.notes ?? "");
  const wasteN = Math.max(0, Math.min(50, Math.round(Number(waste) || 0)));
  const body = () => ({ reading: r, wastePercent: wasteN, label, notes });
  const save = useMutation({ mutationFn: () => (m ? patch<MeasurementItem>(`/api/measurements/${m.id}`, body()) : post<MeasurementItem>(`/api/jobs/${jobId}/measurements/manual`, body())), onSuccess: onSaved });
  const again = useMutation({ mutationFn: () => post(`/api/measurements/${m!.id}/read-again`), onSuccess: async () => (await qc.invalidateQueries({ queryKey: ["measurements", m!.jobId] }), onClose()) });
  const remove = useMutation({ mutationFn: () => del(`/api/measurements/${m!.id}`), onSuccess: async () => (await qc.invalidateQueries({ queryKey: ["measurements", m!.jobId] }), onClose()) });
  const edit = can("materials.order");
  const pitchSum = r.pitches.reduce((s, p) => s + p.areaSqFt, 0);
  const itemQty = (key: string) => r.items?.find((i) => i.key === key)?.quantity ?? 0;
  const setItem = (key: string, quantity: number) => setR({ ...r, items: [...(r.items ?? []).filter((i) => i.key !== key), ...(quantity ? [{ key, quantity }] : [])] });
  const src = (k: string) => r.sources?.[k];
  return (
    <Dialog
      wide
      title={m ? "Roof measurements" : "Enter roof measurements"}
      onClose={onClose}
      footer={
        edit ? (
          <>
            {m && (
              <Button variant="quiet" busy={remove.isPending} onClick={() => window.confirm("Remove these measurements? A report's PDF stays in the job's files.") && remove.mutate()}>
                Remove
              </Button>
            )}
            {m?.kind === "report" && (
              <Button variant="quiet" busy={again.isPending} onClick={() => again.mutate()}>
                Read again
              </Button>
            )}
            <span className="flex-1" />
            <Button variant="primary" busy={save.isPending} disabled={!r.totalAreaSqFt} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {(save.error || again.error) && <Alert>{errorMessage(save.error ?? again.error)}</Alert>}
        {m?.problems.map((p) => (
          <Alert key={p} tone="info">
            {p}
          </Alert>
        ))}
        {m?.kind === "scope" && <Alert tone="info">From the insurance scope: the roof area's own measurement summary, plus what its lines say (shown under each number). Check them against a report when you have one.</Alert>}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Which roof (optional)" value={label} disabled={!edit} onChange={(e) => setLabel(e.target.value)} placeholder="Main house, Garage, Shed…" />
          {m?.kind === "report" ? (
            <SelectField label="Report" value={r.source} disabled={!edit} onChange={(e) => setR({ ...r, source: e.target.value as MeasurementReading["source"] })}>
              {MEASUREMENT_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {MEASUREMENT_SOURCE_LABELS[s]}
                </option>
              ))}
            </SelectField>
          ) : (
            <div />
          )}
          <div />
          <NumField label="Roof area (sq ft, no waste)" value={r.totalAreaSqFt} disabled={!edit} onChange={(n) => setR({ ...r, totalAreaSqFt: n })} hint={src("totalAreaSqFt")} />
          <Field label="Waste (%)" inputMode="numeric" disabled={!edit} value={waste} onChange={(e) => setWaste(e.target.value)} hint={src("wastePercent") ?? (r.suggestedWastePercent !== null ? `Suggested: ${r.suggestedWastePercent}%` : undefined)} />
          <div className="self-end pb-2 text-sm text-ink-soft">{formatQuantity(squaresWithWaste(r.totalAreaSqFt, wasteN))} squares with waste</div>
        </div>
        <div>
          <div className="mb-1 text-sm font-semibold text-ink-soft">Area by pitch {r.pitches.length > 0 && <span className="font-normal text-muted">({pitchSum.toLocaleString("en-US")} sq ft)</span>}</div>
          <div className="flex flex-col gap-2">
            {r.pitches.map((p, i) => (
              <div key={i} className="grid grid-cols-[100px_1fr_auto] items-center gap-2">
                <input aria-label="Pitch" className="rounded-[var(--radius-control)] border border-line px-2 py-1.5 text-sm" disabled={!edit} value={p.pitch} onChange={(e) => setR({ ...r, pitches: r.pitches.map((x, k) => (k === i ? { ...x, pitch: e.target.value } : x)) })} />
                <input aria-label="Area" inputMode="numeric" className="rounded-[var(--radius-control)] border border-line px-2 py-1.5 text-sm" disabled={!edit} value={String(p.areaSqFt)} onChange={(e) => setR({ ...r, pitches: r.pitches.map((x, k) => (k === i ? { ...x, areaSqFt: Math.max(0, Math.round(Number(e.target.value) || 0)) } : x)) })} />
                {edit && (
                  <button className="text-sm text-muted hover:text-ink" aria-label="Remove pitch" onClick={() => setR({ ...r, pitches: r.pitches.filter((_, k) => k !== i) })}>
                    ×
                  </button>
                )}
              </div>
            ))}
            {edit && (
              <button className="self-start text-sm font-semibold text-brand hover:underline" onClick={() => setR({ ...r, pitches: [...r.pitches, { pitch: "6/12", areaSqFt: 0 }] })}>
                Add a pitch
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LENGTHS.map(([k, name]) => (
            <NumField key={k} label={`${name} (ft)`} value={Number(r[k] ?? 0)} disabled={!edit} onChange={(n) => setR({ ...r, [k]: n })} hint={src(k)} />
          ))}
        </div>
        {defs.length > 0 && (
          <div>
            <div className="mb-1 text-sm font-semibold text-ink-soft">
              Other items <span className="font-normal text-muted">(Settings → Materials → Measurement items)</span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {defs.map((d) => (
                <NumField key={d.key} label={`${d.name}${d.unit === "ft" ? " (ft)" : ""}`} value={itemQty(d.key)} disabled={!edit} onChange={(n) => setItem(d.key, n)} hint={src(`item:${d.key}`)} />
              ))}
            </div>
          </div>
        )}
        <TextAreaField label="Notes" value={notes} disabled={!edit} onChange={(e) => setNotes(e.target.value)} placeholder="Where these came from: measured on site, pitch gauge…" />
      </div>
    </Dialog>
  );
}

function JobOrders({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ["material-orders", jobId], queryFn: () => get<MaterialOrderItem[]>(`/api/jobs/${jobId}/material-orders`) });
  const [creating, setCreating] = useState(false);
  return (
    <Panel>
      <PanelHeader
        title="Material orders"
        description="Built from a measurement report and the catalog in Settings → Materials. Check the quantities, then send the PDF to the supplier."
        action={
          can("materials.order") && (
            <Button variant="primary" className="px-3 py-1.5" onClick={() => setCreating(true)}>
              New order
            </Button>
          )
        }
      />
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !list.data?.length ? (
        <Empty>No material orders yet.</Empty>
      ) : (
        <OrderList orders={list.data} />
      )}
      {creating && <NewOrderDialog jobId={jobId} onClose={() => setCreating(false)} onMade={(o) => navigate(`/material-orders/${o.id}`)} />}
    </Panel>
  );
}

function OrderList({ orders, showJob }: { orders: MaterialOrderItem[]; showJob?: boolean }) {
  return (
    <ul>
      {orders.map((o) => (
        <li key={o.id} className="border-b border-mist last:border-0">
          <Link to={`/material-orders/${o.id}`} className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-mist/50">
            <OrderStatus status={o.status} />
            <span className="font-semibold">Order {o.number}</span>
            {showJob && (
              <span className="text-sm">
                Job #{o.jobNumber} {o.customerName}
              </span>
            )}
            <span className="text-sm text-muted">{[o.supplier, `${o.lines.length} item${o.lines.length === 1 ? "" : "s"}`, o.status === "delivered" ? `delivered ${formatDateTime(o.deliveredAt)}` : deliveryText(o.deliveryDate)].filter(Boolean).join(" · ")}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NewOrderDialog({ jobId, onClose, onMade }: { jobId: string; onClose: () => void; onMade: (o: MaterialOrderItem) => void }) {
  const measurements = useMeasurements(jobId);
  const catalog = useQuery({ queryKey: ["material-catalog"], queryFn: () => get<MaterialCatalogResponse>("/api/material-catalog") });
  const read = (measurements.data ?? []).filter((m) => m.status === "read");
  const templates = catalog.data?.templates ?? [];
  const [mid, setMid] = useState<string>("");
  const [tid, setTid] = useState<string | null>(null);
  const measurementId = mid || read[0]?.id || "";
  const templateId = tid ?? templates[0]?.id ?? "";
  const make = useMutation({ mutationFn: () => post<MaterialOrderItem>(`/api/jobs/${jobId}/material-orders`, { measurementId: measurementId || null, templateId: templateId || null }), onSuccess: onMade });
  return (
    <Dialog
      title="New material order"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={make.isPending} onClick={() => make.mutate()}>
            Make the order
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {make.error && <Alert>{errorMessage(make.error)}</Alert>}
        {measurements.isLoading || catalog.isLoading ? (
          <Loading />
        ) : (
          <>
            <SelectField label="Start from a template" value={templateId} onChange={(e) => setTid(e.target.value)} hint={templates.length ? undefined : "No templates yet: make them in Settings → Materials, or save an order as one."}>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
              <option value="">None: start empty</option>
            </SelectField>
            {templateId && (
              <SelectField label="Count from measurement report" value={measurementId} onChange={(e) => setMid(e.target.value)}>
                {read.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.file?.name ?? "Report"} ({formatQuantity(squaresWithWaste(m.reading!.totalAreaSqFt, m.wastePercent))} SQ)
                  </option>
                ))}
                <option value="">None: counted lines start at 0</option>
              </SelectField>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Open material orders across jobs, by delivery date. */
export function MaterialOrdersPage() {
  const [all, setAll] = useState(false);
  const q = useQuery({ queryKey: ["material-orders", "all", all], queryFn: () => get<MaterialOrderItem[]>(`/api/material-orders${all ? "?all=1" : ""}`) });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Material orders"
        description="Drafts and orders waiting on delivery, soonest first. Start an order from a job's Materials tab."
        action={
          <Button variant="secondary" onClick={() => setAll(!all)}>
            {all ? "Open orders only" : "Show all"}
          </Button>
        }
      />
      <Panel>{q.isLoading ? <Loading /> : !q.data?.length ? <Empty>No open material orders.</Empty> : <OrderList orders={q.data} showJob />}</Panel>
    </div>
  );
}

/** One material order: lines, supplier, delivery; place it, then mark it delivered. */
export function MaterialOrderPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["material-order", id], queryFn: () => get<MaterialOrderItem>(`/api/material-orders/${id}`) });
  const navigate = useNavigate();
  // A draft work order with these materials: opened in the job's work orders to pick the sub, labor, the date, and send.
  const makeWo = useMutation({
    mutationFn: () => post<{ id: string; jobId: string }>(`/api/material-orders/${id}/work-order`),
    onSuccess: (w) => navigate(`/jobs/${w.jobId}?tab=workorders&editWorkOrder=${w.id}`),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert>{errorMessage(q.error)}</Alert>;
  const o = q.data!;
  const set = (d: MaterialOrderItem) => (qc.setQueryData(["material-order", id], d), qc.invalidateQueries({ queryKey: ["material-orders"] }));
  const editable = can("materials.order") && (o.status === "draft" || o.status === "ordered");
  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="text-sm text-muted">
          <Link to={`/jobs/${o.jobId}`} className="hover:text-ink">
            Job #{o.jobNumber} {o.customerName}
          </Link>
          {o.address && ` · ${o.address}`}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="page-title">Material order {o.number}</h1>
          <OrderStatus status={o.status} />
          <span className="flex-1" />
          {can("workorders.edit") && o.status !== "cancelled" && (
            <Button busy={makeWo.isPending} onClick={() => makeWo.mutate()}>
              Make work order
            </Button>
          )}
        </div>
        {o.workOrders.length > 0 && (
          <div className="mt-1 text-sm text-muted">
            Made from it:{" "}
            {o.workOrders.map((w, i) => (
              <span key={w.id}>
                {i > 0 && ", "}
                <Link to={`/jobs/${o.jobId}?tab=workorders&workOrder=${w.id}`} className="font-semibold text-brand hover:underline">
                  Work order #{w.number}
                </Link>
              </span>
            ))}
          </div>
        )}
        {makeWo.error && <Alert>{errorMessage(makeWo.error)}</Alert>}
      </div>
      <OrderEditor key={`${o.id}-${o.status}`} o={o} editable={editable} onChange={set} />
    </div>
  );
}

function OrderEditor({ o, editable, onChange }: { o: MaterialOrderItem; editable: boolean; onChange: (d: MaterialOrderItem) => void }) {
  const { can } = useSession();
  const catalog = useQuery({ queryKey: ["material-catalog"], queryFn: () => get<MaterialCatalogResponse>("/api/material-catalog") });
  const [supplier, setSupplier] = useState(o.supplier);
  const [date, setDate] = useState(o.deliveryDate ?? "");
  const [notes, setNotes] = useState(o.deliveryNotes);
  const [lines, setLines] = useState<MaterialLine[]>(o.lines);
  const [saved, setSaved] = useState(false);
  const [searching, setSearching] = useState(false);
  const [templateMsg, setTemplateMsg] = useState<string | null>(null);
  const body = { supplier, deliveryDate: date || null, deliveryNotes: notes, lines };
  const cost = linesCost(lines);
  const byId = new Map((catalog.data?.items ?? []).map((it) => [it.id, it]));
  const problems = catalog.data ? orderProblems(lines, catalog.data.items) : [];
  const asTemplate = useMutation({
    mutationFn: async (name: string) => {
      await patch(`/api/material-orders/${o.id}`, body);
      return post<{ name: string }>(`/api/material-orders/${o.id}/template`, { name });
    },
    onSuccess: (t) => setTemplateMsg(`Saved as the template "${t.name}". Set which lines are counted from a report in Settings → Materials.`),
  });
  const save = useMutation({ mutationFn: () => patch<MaterialOrderItem>(`/api/material-orders/${o.id}`, body), onSuccess: (d) => (onChange(d), setSaved(true)) });
  const place = useMutation({
    mutationFn: async () => {
      await patch(`/api/material-orders/${o.id}`, body);
      return post<MaterialOrderItem>(`/api/material-orders/${o.id}/ordered`);
    },
    onSuccess: onChange,
  });
  const delivered = useMutation({ mutationFn: () => post<MaterialOrderItem>(`/api/material-orders/${o.id}/delivered`), onSuccess: onChange });
  const abc = useQuery({ queryKey: ["abc"], queryFn: () => get<AbcStatus>("/api/abc") });
  const [sending, setSending] = useState(false);
  const canSend = editable && o.status === "draft" && !!abc.data?.ordering && !!abc.data.orderingAllowed;
  const cancel = useMutation({ mutationFn: () => post<MaterialOrderItem>(`/api/material-orders/${o.id}/cancel`), onSuccess: onChange });
  const err = save.error ?? place.error ?? delivered.error ?? cancel.error ?? asTemplate.error;
  const edit = (i: number, p: Partial<MaterialLine>) => (setSaved(false), setLines(lines.map((l, k) => (k === i ? { ...l, ...p } : l))));
  const linesLocked = !editable || o.status !== "draft";

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Panel>
        <PanelHeader title="Items" description={linesLocked ? undefined : "Quantities are whole units, rounded up from the measurements. Set an item to 0 to leave it off."} />
        <ul>
          {lines.map((l, i) => (
            <li key={i} className="grid grid-cols-[1fr_80px_60px_90px_auto] items-end gap-2 border-b border-mist px-5 py-2.5">
              {linesLocked ? (
                <>
                  <span className="text-sm">
                    {l.name}
                    {(l.itemNumber || l.note) && <span className="block text-xs text-muted">{[l.itemNumber && `ABC #${l.itemNumber}`, l.note].filter(Boolean).join(" · ")}</span>}
                  </span>
                  <span className="text-right text-sm font-semibold">{l.quantity}</span>
                  <span className="text-sm text-muted">{l.unit}</span>
                  <span className="text-right text-sm text-ink-soft">{l.unitCents === null || l.unitCents === undefined ? "" : formatCents(l.unitCents * l.quantity)}</span>
                  <span />
                </>
              ) : (
                <>
                  <Field label="Item" value={l.name} maxLength={160} hint={l.itemNumber ? `ABC #${l.itemNumber}` : undefined} onChange={(e) => edit(i, { name: e.target.value })} />
                  <Field label="Qty" inputMode="numeric" value={String(l.quantity)} onChange={(e) => edit(i, { quantity: Math.max(0, Math.min(100_000, Math.round(Number(e.target.value) || 0))) })} />
                  <Field label="Unit" value={l.unit} maxLength={10} onChange={(e) => edit(i, { unit: e.target.value })} />
                  <span className="pb-2 text-right text-sm text-ink-soft" title={l.unitCents === null || l.unitCents === undefined ? "No price" : `${formatCents(l.unitCents)} per ${l.unit}`}>
                    {l.unitCents === null || l.unitCents === undefined ? "—" : formatCents(l.unitCents * l.quantity)}
                  </span>
                  <Button variant="quiet" className="mb-0.5" aria-label="Remove item" onClick={() => (setSaved(false), setLines(lines.filter((_, k) => k !== i)))}>
                    ×
                  </Button>
                  <LineOptions line={l} item={l.itemKey ? byId.get(l.itemKey) : undefined} onChange={(next) => (setSaved(false), setLines(lines.map((x, k) => (k === i ? next : x))))} />
                </>
              )}
            </li>
          ))}
        </ul>
        {lines.length > 0 && (
          <div className="flex justify-between gap-3 border-b border-mist px-5 py-2.5 text-sm">
            <span className="text-muted">Estimated cost at catalog prices{cost.unpriced.length > 0 && ` (${cost.unpriced.length} without a price)`}</span>
            <span className="font-semibold">{formatCents(cost.cents)}</span>
          </div>
        )}
        {!linesLocked && (
          <div className="flex flex-wrap items-end gap-2 px-5 py-3">
            <SelectField
              label="Add from the catalog"
              value=""
              onChange={(e) => {
                const it = catalog.data?.items.find((x) => x.id === e.target.value);
                if (it) (setSaved(false), setLines([...lines, lineFor(it, 1)]));
              }}
            >
              <option value="">Pick an item…</option>
              {catalog.data?.items.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name} ({it.unit})
                </option>
              ))}
            </SelectField>
            <Button variant="secondary" onClick={() => setSearching(true)}>
              Search ABC
            </Button>
            <Button variant="quiet" onClick={() => (setSaved(false), setLines([...lines, { itemKey: null, name: "Item", unit: "EA", quantity: 1, note: "" }]))}>
              Add other item
            </Button>
          </div>
        )}
        {searching && (
          <AbcSearchDialog
            pickLabel="Add to order"
            onClose={() => setSearching(false)}
            onPick={(it) => {
              setSaved(false);
              setLines((ls) => [...ls, lineFor(it, 1)]);
              setSearching(false);
            }}
          />
        )}
      </Panel>
      <aside className="flex flex-col gap-4">
        <Panel className="flex flex-col gap-3 px-5 py-4">
          <Field label="Supplier" value={supplier} disabled={!editable} maxLength={160} onChange={(e) => (setSaved(false), setSupplier(e.target.value))} />
          <Field label="Delivery date" type="date" value={date} disabled={!editable} onChange={(e) => (setSaved(false), setDate(e.target.value))} hint="Shows on the calendar once ordered." />
          <TextAreaField label="Delivery notes" value={notes} disabled={!editable} rows={3} maxLength={2000} onChange={(e) => (setSaved(false), setNotes(e.target.value))} placeholder="Drop in the driveway; gate code..." />
          {err && <Alert>{errorMessage(err)}</Alert>}
          {saved && <Alert tone="ok">Saved.</Alert>}
          {editable && (
            <Button busy={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          )}
        </Panel>
        <Panel className="flex flex-col gap-2 px-5 py-4">
          <a href={o.pdfUrl ?? `/api/material-orders/${o.id}/preview.pdf`} target="_blank" rel="noreferrer" className="inline-flex justify-center rounded-[var(--radius-control)] bg-mist px-4 py-2 text-sm font-semibold hover:bg-line">
            {o.pdfUrl ? "PDF as ordered" : "Preview PDF"}
          </a>
          {editable && o.status === "draft" && problems.length > 0 && (
            <Alert tone="info">
              Before it's ordered:
              <ul className="mt-1 list-disc pl-5">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Alert>
          )}
          <AbcState o={o} onChange={onChange} />
          {canSend && !o.abc?.unknown && (
            <Button
              variant="primary"
              disabled={problems.length > 0}
              onClick={async () => {
                // What's on screen is what goes.
                await patch(`/api/material-orders/${o.id}`, body);
                setSaved(false);
                setSending(true);
              }}
            >
              Send to ABC…
            </Button>
          )}
          {editable && o.status === "draft" && !o.abc?.unknown && (
            <Button variant={canSend ? "secondary" : "primary"} busy={place.isPending} disabled={problems.length > 0} onClick={() => place.mutate()}>
              Mark as ordered
            </Button>
          )}
          {editable && o.status === "draft" && <p className="text-xs text-muted">{canSend ? "Send to ABC places the order with ABC from here. Ordering another way (phone, email, their portal)? Mark it ordered instead." : "Send the PDF to the supplier (email or their portal), then mark it ordered."} The PDF is saved to the job's files.</p>}
          {sending && <SendToAbcDialog o={o} onClose={() => setSending(false)} onSent={(d) => (setSending(false), onChange(d))} />}
          {editable && o.status === "ordered" && (
            <Button variant="primary" busy={delivered.isPending} onClick={() => delivered.mutate()}>
              Mark delivered
            </Button>
          )}
          {editable && (
            <Button variant="quiet" busy={cancel.isPending} onClick={() => window.confirm("Cancel this order?") && cancel.mutate()}>
              Cancel order
            </Button>
          )}
          {can("materials.order") && lines.some((l) => l.itemKey) && (
            <Button
              variant="quiet"
              busy={asTemplate.isPending}
              onClick={() => {
                const name = window.prompt("Template name", "");
                if (name?.trim()) asTemplate.mutate(name.trim());
              }}
            >
              Save as a template
            </Button>
          )}
          {templateMsg && <p className="text-xs text-ok">{templateMsg}</p>}
          {o.orderedAt && <p className="text-xs text-muted">Ordered {formatDateTime(o.orderedAt)}</p>}
          {o.deliveredAt && <p className="text-xs text-muted">Delivered {formatDateTime(o.deliveredAt)}</p>}
        </Panel>
      </aside>
    </div>
  );
}

/** A product line's option (color or size, from ABC) and, for cut-to-length items, its length. */
/** Where an order sent from the CRM stands at ABC (or why sending failed). */
function AbcState({ o, onChange }: { o: MaterialOrderItem; onChange: (d: MaterialOrderItem) => void }) {
  const { can } = useSession();
  const check = useMutation({ mutationFn: () => post<MaterialOrderItem>(`/api/material-orders/${o.id}/abc/check`), onSuccess: onChange });
  const reset = useMutation({ mutationFn: () => post<MaterialOrderItem>(`/api/material-orders/${o.id}/abc/reset`), onSuccess: onChange });
  const a = o.abc;
  if (!a) return null;
  if (!a.confirmation)
    return (
      <Alert tone={a.unknown ? "danger" : "info"}>
        {a.error}
        {a.unknown && can("materials.order") && (
          <div className="mt-2">
            <Button variant="quiet" className="px-2 py-1" busy={reset.isPending} onClick={() => window.confirm("Checked with ABC and it isn't there? Sending again could order it twice if it is.") && reset.mutate()}>
              Not at ABC: let me send again
            </Button>
          </div>
        )}
        {reset.error && <div className="mt-1">{errorMessage(reset.error)}</div>}
      </Alert>
    );
  return (
    <div className="rounded-[var(--radius-control)] bg-mist/60 px-3 py-2 text-sm">
      <div className="font-semibold">Sent to ABC{a.branch ? ` ${a.branch.name}` : ""}{a.sentBy ? ` by ${a.sentBy}` : ""}</div>
      <div>
        Confirmation {a.confirmation}
        {a.orderNumber && ` · order ${a.orderNumber}`}
      </div>
      <div>
        {a.status || "Requested"}
        {a.event && a.event !== a.status ? ` · ${a.event}` : ""}
      </div>
      {a.deliveryService && <div className="text-xs text-muted">{ABC_DELIVERY_SERVICE_LABELS[a.deliveryService]}{a.deliveryTime ? `, ${ABC_DELIVERY_TIME_LABELS[a.deliveryTime].toLowerCase()}` : ""}</div>}
      <div className="mt-1 flex items-center gap-2 text-xs text-muted">
        {a.checkedAt ? `Checked ${formatDateTime(a.checkedAt)}` : "Not checked yet"}
        <Button variant="quiet" className="px-2 py-0.5 text-xs" busy={check.isPending} onClick={() => check.mutate()}>
          Check now
        </Button>
      </div>
      {check.error && <p className="text-xs text-danger">{errorMessage(check.error)}</p>}
      {o.status === "ordered" && <p className="mt-1 text-xs text-muted">Changes after sending (items, date) don't go to ABC: call the branch.</p>}
    </div>
  );
}

/** Looks over exactly what goes to ABC, then sends it once. */
function SendToAbcDialog({ o, onClose, onSent }: { o: MaterialOrderItem; onClose: () => void; onSent: (d: MaterialOrderItem) => void }) {
  const [service, setService] = useState<AbcDeliveryService>("OTR");
  const [time, setTime] = useState<AbcDeliveryTime>("AT");
  // The branch for this order: the company's usual one to start (Settings → Materials).
  const abcStatus = useQuery({ queryKey: ["abc"], queryFn: () => get<AbcStatus>("/api/abc") });
  const [branch, setBranch] = useState("");
  const usual = abcStatus.data?.branch?.number ?? "";
  const opts = { deliveryService: service, deliveryTime: time, ...(branch && branch !== usual ? { branchNumber: branch } : {}) };
  const preview = useQuery({ queryKey: ["abc-preview", o.id, service, time, branch], queryFn: () => post<AbcOrderPreview>(`/api/material-orders/${o.id}/abc/preview`, opts), retry: false });
  const send = useMutation({ mutationFn: () => post<MaterialOrderItem>(`/api/material-orders/${o.id}/abc/send`, opts), onSuccess: onSent });
  const p = preview.data;
  return (
    <Dialog
      title="Send to ABC Supply"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={send.isPending} disabled={!p || p.problems.length > 0 || send.isSuccess} onClick={() => send.mutate()}>
            {p?.sandbox ? "Send test order" : "Send order to ABC"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        {preview.error && <Alert>{errorMessage(preview.error)}</Alert>}
        {(abcStatus.data?.branches.length ?? 0) > 1 && (
          <SelectField label="Branch" value={branch || usual} onChange={(e) => setBranch(e.target.value)} hint="For this order only; the usual branch stays as set in Settings → Materials.">
            {abcStatus.data!.branches.map((b) => (
              <option key={b.number} value={b.number}>
                {b.name}
                {b.number === usual ? " (usual)" : ""}
              </option>
            ))}
          </SelectField>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Delivery" value={service} onChange={(e) => setService(e.target.value as AbcDeliveryService)}>
            {ABC_DELIVERY_SERVICES.map((d) => (
              <option key={d} value={d}>
                {ABC_DELIVERY_SERVICE_LABELS[d]}
              </option>
            ))}
          </SelectField>
          <SelectField label="When that day" value={time} onChange={(e) => setTime(e.target.value as AbcDeliveryTime)}>
            {ABC_DELIVERY_TIMES.map((d) => (
              <option key={d} value={d}>
                {ABC_DELIVERY_TIME_LABELS[d]}
              </option>
            ))}
          </SelectField>
        </div>
        {preview.isLoading && <Loading />}
        {p && (
          <>
            {p.sandbox && <Alert tone="info">ABC's sandbox: this is a test order, not a real one.</Alert>}
            {p.problems.length > 0 && (
              <Alert>
                Fix these first:
                <ul className="mt-1 list-disc pl-5">
                  {p.problems.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </Alert>
            )}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted">Account</dt>
              <dd>{p.shipTo}</dd>
              <dt className="text-muted">Branch</dt>
              <dd>{p.branch}</dd>
              <dt className="text-muted">PO</dt>
              <dd>{p.purchaseOrder}</dd>
              <dt className="text-muted">Job</dt>
              <dd>{p.jobName}</dd>
              <dt className="text-muted">Deliver to</dt>
              <dd>{p.address}</dd>
              <dt className="text-muted">Date</dt>
              <dd>{p.deliveryDate ?? "To be scheduled by the branch"}</dd>
              {p.deliveryNotes && (
                <>
                  <dt className="text-muted">Notes</dt>
                  <dd className="whitespace-pre-wrap">{p.deliveryNotes}</dd>
                </>
              )}
              <dt className="text-muted">Contact</dt>
              <dd>{[p.contact.name, p.contact.phone, p.contact.email].filter(Boolean).join(" · ")}</dd>
            </dl>
            <table className="w-full text-left">
              <thead className="text-xs text-muted">
                <tr>
                  <th className="py-1 font-medium">Item</th>
                  <th className="py-1 text-right font-medium">Qty</th>
                  <th className="py-1 text-right font-medium">Price</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map((l, i) => (
                  <tr key={i} className="border-t border-mist align-top">
                    <td className="py-1">
                      {l.description}
                      <span className="block text-xs text-muted">{[`#${l.itemNumber}`, l.length, l.note].filter(Boolean).join(" · ")}</span>
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">
                      {l.quantity} {l.unit}
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">{l.unitCents === null ? "—" : formatCents(l.unitCents * l.quantity)}</td>
                  </tr>
                ))}
                <tr className="border-t border-line font-semibold">
                  <td className="py-1" colSpan={2}>
                    Before tax and delivery
                  </td>
                  <td className="py-1 text-right">{formatCents(p.totalCents)}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </Dialog>
  );
}

function LineOptions({ line, item, onChange }: { line: MaterialLine; item: MaterialItem | undefined; onChange: (l: MaterialLine) => void }) {
  if (!item || item.options.length < 1) return null;
  const picked = item.options.find((o) => o.itemNumber === line.itemNumber);
  const many = item.options.length > 1;
  if (!many && !picked?.dimensional) return null;
  return (
    <div className="col-span-full -mt-1 flex flex-wrap items-center gap-2 pb-1">
      {many && (
        <select
          className={`min-w-0 flex-1 rounded-[var(--radius-control)] border bg-white px-2 py-1.5 text-base sm:text-sm ${line.itemNumber ? "border-line" : "border-wait"}`}
          value={line.itemNumber ?? ""}
          onChange={(e) => e.target.value && onChange(withOption(line, item, e.target.value))}
          aria-label={`Color or size for ${item.name}`}
        >
          {!line.itemNumber && <option value="">Pick the color or size…</option>}
          {[...item.options]
            .sort((a, b) => b.timesOrdered - a.timesOrdered || a.label.localeCompare(b.label))
            .map((o) => (
              <option key={o.itemNumber} value={o.itemNumber}>
                {o.label}
                {o.unitCents !== null ? ` · ${formatCents(o.unitCents)}/${o.unit}` : ""}
                {o.timesOrdered ? ` · ordered ${o.timesOrdered}×` : ""}
              </option>
            ))}
        </select>
      )}
      {picked?.dimensional && (
        <label className="flex items-center gap-1.5 text-sm">
          Length
          <input
            className={`w-20 rounded-[var(--radius-control)] border bg-white px-2 py-1.5 text-base sm:text-sm ${line.lengthValue ? "border-line" : "border-wait"}`}
            inputMode="decimal"
            value={line.lengthValue ?? ""}
            onChange={(e) => onChange({ ...line, lengthValue: Number(e.target.value) || null, lengthUom: line.lengthUom ?? "FT" })}
          />
          {line.lengthUom ?? "FT"}
        </label>
      )}
    </div>
  );
}
